import { Bot, MessagesSquare, User } from 'lucide-react';
import { useState } from 'react';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useLineUsers, useSessionMessages, useUserSessions } from '@/hooks/useLineData';
import { formatDateTime } from '@/lib/firestoreTime';
import { cn } from '@/lib/utils';

interface SessionDoc {
  id: string;
  status?: string;
  inquiryType?: string | null;
  startedAt?: unknown;
  closedAt?: unknown;
  messageCount?: number;
  feedback?: { rating?: number } | null;
}

interface MessageDoc {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: unknown;
  toolCalls?: Array<{ name: string; input: unknown }> | null;
}

interface SessionsViewProps {
  selectedUid: string | null;
  onSelectUid: (uid: string) => void;
}

export function SessionsView({ selectedUid, onSelectUid }: SessionsViewProps) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const { data: users, isLoading: usersLoading } = useLineUsers();
  const { data: sessions } = useUserSessions(selectedUid);
  const { data: messages } = useSessionMessages(selectedUid, selectedSessionId);

  return (
    <div className="grid grid-cols-[280px_320px_1fr] gap-4 h-[calc(100vh-260px)]">
      {/* ユーザー列 */}
      <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-y-auto">
        <div className="p-3 border-b border-[var(--border)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
          ユーザー
        </div>
        {usersLoading ? (
          <Spinner />
        ) : (
          users?.map((u) => (
            <button
              type="button"
              key={u.id}
              className={cn(
                'w-full text-left p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors',
                selectedUid === u.id && 'bg-[var(--accent-subtle)]',
              )}
              onClick={() => {
                onSelectUid(u.id);
                setSelectedSessionId(null);
              }}
            >
              <div className="text-[13px] font-medium truncate">
                {u.displayName || '(名無し)'}
              </div>
              <div className="text-[10px] text-[var(--text-muted)] font-mono truncate">
                {u.id}
              </div>
            </button>
          ))
        )}
      </div>

      {/* セッション列 */}
      <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-y-auto">
        <div className="p-3 border-b border-[var(--border)] text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
          セッション
        </div>
        {!selectedUid ? (
          <div className="p-6 text-xs text-[var(--text-muted)] text-center">
            左からユーザーを選択してください
          </div>
        ) : !sessions || sessions.length === 0 ? (
          <div className="p-6 text-xs text-[var(--text-muted)] text-center">
            セッションなし
          </div>
        ) : (
          (sessions as SessionDoc[]).map((s) => (
            <button
              type="button"
              key={s.id}
              className={cn(
                'w-full text-left p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors',
                selectedSessionId === s.id && 'bg-[var(--accent-subtle)]',
              )}
              onClick={() => setSelectedSessionId(s.id)}
            >
              <div className="flex items-center gap-2 mb-1">
                <Badge
                  variant={
                    s.status === 'active'
                      ? 'approved'
                      : s.status === 'closed'
                        ? 'default'
                        : 'danger'
                  }
                >
                  {s.status}
                </Badge>
                {s.inquiryType && <Badge variant="accent">{s.inquiryType}</Badge>}
                {s.feedback?.rating && (
                  <Badge variant={s.feedback.rating >= 4 ? 'approved' : 'danger'}>
                    ★{s.feedback.rating}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-[var(--text-muted)]">
                {formatDateTime(s.startedAt)} / {s.messageCount ?? 0} 件
              </div>
            </button>
          ))
        )}
      </div>

      {/* メッセージ列 */}
      <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] overflow-y-auto p-4 space-y-3">
        {!selectedSessionId ? (
          <EmptyState
            icon={MessagesSquare}
            title="セッションを選択してください"
            description="左でセッションをクリックすると、ここにメッセージが表示されます"
          />
        ) : !messages || messages.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] text-center py-8">
            メッセージがありません
          </p>
        ) : (
          (messages as MessageDoc[]).map((m) => (
            <div
              key={m.id}
              className={cn(
                'flex gap-3',
                m.role === 'user' && 'flex-row',
                m.role === 'assistant' && 'flex-row',
                m.role === 'system' && 'flex-row opacity-60',
              )}
            >
              <div
                className={cn(
                  'size-7 rounded-full flex items-center justify-center flex-shrink-0',
                  m.role === 'user' && 'bg-[var(--accent-subtle)] text-[var(--accent)]',
                  m.role === 'assistant' &&
                    'bg-[var(--accent-green-subtle)] text-[var(--accent-green)]',
                  m.role === 'system' && 'bg-[var(--bg-hover)] text-[var(--text-muted)]',
                )}
              >
                {m.role === 'user' ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 text-xs">
                  <span className="font-semibold">{m.role}</span>
                  <span className="text-[var(--text-muted)]">{formatDateTime(m.timestamp)}</span>
                </div>
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed">
                  {m.content}
                </div>
                {m.toolCalls && m.toolCalls.length > 0 && (
                  <details className="mt-2 text-[11px]">
                    <summary className="cursor-pointer text-[var(--text-muted)]">
                      Tool calls ({m.toolCalls.length})
                    </summary>
                    <pre className="mt-1 p-2 bg-[var(--bg-elevated)] rounded-sm overflow-auto text-[10px]">
                      {JSON.stringify(m.toolCalls, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
